import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Grid,
  Divider,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CategoryIcon from '@mui/icons-material/Category';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    loadProduct();
    if (user) {
      checkFavorite();
    }
  }, [id, user]);

  const loadProduct = async () => {
    const viewKey = `product_view_${id}`;
    // Reservar logo (síncrono) para evitar 2 chamadas simultâneas contarem 2 views (ex.: Strict Mode)
    const deveRegistrarView = !sessionStorage.getItem(viewKey);
    if (deveRegistrarView) {
      sessionStorage.setItem(viewKey, 'pending');
    }

    try {
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);

      if (deveRegistrarView && sessionStorage.getItem(viewKey) === 'pending') {
        try {
          let sessionToken = sessionStorage.getItem('apuro_view_session');
          if (!sessionToken) {
            sessionToken = (typeof crypto !== 'undefined' && crypto.randomUUID)
              ? crypto.randomUUID()
              : `s${Date.now()}-${Math.random().toString(36).slice(2)}`;
            sessionStorage.setItem('apuro_view_session', sessionToken);
          }
          await api.post(`/products/${id}/view`, { session_token: sessionToken });
          sessionStorage.setItem(viewKey, '1');
        } catch (err) {
          sessionStorage.removeItem(viewKey);
        }
      }
    } catch (error) {
      if (deveRegistrarView) sessionStorage.removeItem(viewKey);
      console.error('Erro ao carregar produto:', error);
    }
  };

  const checkFavorite = async () => {
    try {
      const response = await api.get(`/favorites/check/${id}`);
      setIsFavorite(response.data.isFavorite);
    } catch (error) {
      console.error('Erro ao verificar favorito:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        await api.delete(`/favorites/${id}`);
        setIsFavorite(false);
      } else {
        await api.post(`/favorites/${id}`);
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Erro ao favoritar:', error);
    }
  };

  if (!product) {
    return (
      <Container>
        <Typography>Carregando...</Typography>
      </Container>
    );
  }

  return (
    <Box>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 6, pt: 10 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ 
            mb: 3,
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'background.default',
              color: 'primary.main',
            },
          }}
        >
          Voltar
        </Button>

        <Grid container spacing={6}>
          <Grid item xs={12} md={7}>
            {product.image_url ? (
              <Paper 
                elevation={0}
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  borderRadius: 2,
                }}
              >
                <Box
                  component="img"
                  src={product.image_url}
                  alt={product.name}
                  sx={{ 
                    width: '100%', 
                    height: 'auto', 
                    display: 'block',
                    maxHeight: '600px',
                    objectFit: 'contain',
                  }}
                />
              </Paper>
            ) : (
              <Paper 
                elevation={0}
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  height: '500px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'background.default',
                  borderRadius: 2,
                }}
              >
                <CategoryIcon sx={{ fontSize: 80, color: 'primary.main', opacity: 0.3 }} />
              </Paper>
            )}
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
                <Typography 
                  variant="h3" 
                  component="h1"
                  sx={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontWeight: 700,
                    flexGrow: 1,
                    pr: 2,
                  }}
                >
                  {product.name}
                </Typography>
                {user && (
                  <IconButton
                    onClick={toggleFavorite}
                    sx={{
                      color: (theme) => isFavorite ? theme.palette.primary.main : theme.palette.text.secondary,
                      border: (theme) => `2px solid ${isFavorite ? theme.palette.primary.main : theme.palette.divider}`,
                      '&:hover': {
                        backgroundColor: 'background.default',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                      },
                    }}
                    size="large"
                  >
                    {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                  </IconButton>
                )}
              </Box>

              <Chip 
                label={product.category} 
                sx={{ 
                  mb: 3,
                  backgroundColor: 'background.default',
                  color: 'primary.main',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  height: '32px',
                }} 
              />

              <Divider sx={{ my: 3 }} />

              <Typography 
                variant="h6" 
                gutterBottom
                sx={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontWeight: 600,
                  mb: 2,
                  color: 'primary.main',
                }}
              >
                Descrição
              </Typography>
              <Typography 
                variant="body1" 
                paragraph
                sx={{
                  lineHeight: 1.8,
                  color: 'text.secondary',
                  fontSize: '1rem',
                }}
              >
                {product.description || 'Produto exclusivo de alto padrão, selecionado especialmente para nossa curadoria.'}
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography 
                variant="h6" 
                gutterBottom
                sx={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontWeight: 600,
                  mb: 2,
                  color: 'primary.main',
                }}
              >
                Loja
              </Typography>
              <Button
                component={Link}
                to={`/store/${product.store_id}`}
                variant="outlined"
                color="primary"
                sx={{ 
                  mt: 1,
                  '&:hover': {
                    backgroundColor: (theme) => `${theme.palette.primary.main}0D`,
                  },
                }}
              >
                {product.store_name}
              </Button>

              <Box sx={{ 
                mt: 4,
                p: 2,
                backgroundColor: 'background.default',
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
                borderRadius: 1,
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Visualizações:</strong> {product.views_count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Favoritos:</strong> {product.favorites_count || 0}
                </Typography>
                {Number(product.engagement_score) > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    <strong>Score de Engajamento:</strong> {Number(product.engagement_score).toFixed(1)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductDetail;
